import styled from "@emotion/styled"
import Image from "next/image"
import { ComponentProps, ReactNode } from "react"
import { BORDER_GREY_COLOR } from "theme/colors"
import Group from "./Group"
import RoundedButton from "./RoundedButton"
import { Value } from "@stringtale/react"

const Root = styled(Group)<{ transparent: boolean }>`
  width: max-content;
  border-radius: 8px;
  justify-content: flex-start;
  align-items: stretch;
  text-decoration: "underline";
  display: flex;

  border: ${(p) => {
    if (p.transparent) {
      return `1px solid ${BORDER_GREY_COLOR}80`
    } else {
      return `1px solid ${BORDER_GREY_COLOR}`
    }
  }};

  background-color: ${(p) => {
    if (p.transparent) {
      return "#FFFFFF1A"
    } else {
      return "white"
    }
  }};
`

const ImageWrapper = styled.div<{
  transparent: boolean
}>`
  border-radius: 8px 0 0 8px;
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 24px;
  padding-bottom: 24px;
  background-color: ${(p) => {
    if (p.transparent) {
      return `${BORDER_GREY_COLOR}33`
    } else {
      return `${BORDER_GREY_COLOR}80`
    }
  }};
  display: flex;
  align-items: center;
  text-decoration: underline;
  max-width: fit-content;
  flex-grow: 1;
  box-sizing: border-box;
`

const ContentWrapper = styled.div<{ fillWidth: boolean }>`
  width: ${(p) => (p.fillWidth ? "100%" : "auto")};
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
`

type IconName = "question" | "exclamation" | "tip"

type IconStyle = "light" | "dark"

const supportTipIcon = function ({
  iconName,
  iconStyle,
}: {
  iconName: IconName
  iconStyle: IconStyle
}) {
  if (iconStyle === "dark") {
    switch (iconName) {
      case "exclamation":
        return "/icons/support_tip/ico_support_emark.svg"
      case "question":
        return "/icons/support_tip/ico_support_qmark.svg"
      case "tip":
        return "/icons/support_tip/ico_support_tiptip.svg"
    }
  } else {
    switch (iconName) {
      case "exclamation":
        return "/icons/support_tip/ico_support_emark_wh.svg"
      case "question":
        return "/icons/support_tip/ico_support_qmark_wh.svg"
      case "tip":
        return "/icons/support_tip/ico_support_tiptip_wh.svg"
    }
  }
}

export default function SupportTip({
  href,
  iconName = "question",
  iconStyle = "dark",
  children,
  buttonLabel,
  transparent = false,
  style,
  fillWidth = false,
  ...props
}: {
  href?: string
  iconName?: IconName
  iconStyle?: IconStyle
  children: ReactNode
  buttonLabel?: ReactNode
  transparent?: boolean
  fillWidth?: boolean
} & ComponentProps<"div">) {
  return (
    <Root transparent={transparent} style={style} {...props}>
      <ImageWrapper transparent={transparent}>
        <Image
          src={supportTipIcon({ iconName, iconStyle })}
          width={37}
          height={34}
          alt="Tip icon"
        />
      </ImageWrapper>
      <ContentWrapper fillWidth={fillWidth}>
        {children}
        {!!href && (
          <a href={href}>
            <RoundedButton color="orange" size="small">
              {buttonLabel || (
                <Value name="apps.web.src.content-blocks.components.contentblocks.lees_verder">
                  Lees verder
                </Value>
              )}
            </RoundedButton>
          </a>
        )}
      </ContentWrapper>
    </Root>
  )
}
